import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Trash2, Loader2, ImageIcon } from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TileImageEditor = ({ tileId, label, current, onClose, onSaved }) => {
  const [imagePath, setImagePath] = useState(current?.image_path || null);
  const [size, setSize] = useState(Number(current?.size) || 56);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("صورة فقط"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("أقل من 5 ميغا"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload-image`, fd);
      setImagePath(data.path);
      toast.success("تم رفع الصورة");
    } catch {
      toast.error("فشل الرفع");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/tile-images/${tileId}`, {
        image_path: imagePath,
        size,
      });
      toast.success("تم الحفظ");
      onSaved(data);
    } catch {
      toast.error("فشل الحفظ");
    } finally { setSaving(false); }
  };

  const clearImage = async () => {
    if (!(await confirmDialog("استعادة الأيقونة الأصلية وحذف الصورة؟"))) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/tile-images/${tileId}`);
      toast.success("تم إعادة الأيقونة الأصلية");
      onSaved({ image_path: null, size: 56 });
    } catch {
      toast.error("فشل الحذف");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
      dir="rtl"
      data-testid="tile-image-editor"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between pb-3 border-b-2 border-[#1E3A5F]/20 mb-4">
          <div>
            <h3 className="font-bold text-lg text-[#1E3A5F]">تخصيص أيقونة القسم</h3>
            <p className="text-xs text-[#5C6E64]">القسم: <span className="font-bold">{label}</span></p>
          </div>
          <button onClick={onClose} className="text-[#5C6E64] hover:text-[#1E3A5F]" data-testid="tile-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Live preview */}
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-5 mb-4 flex flex-col items-center gap-2 min-h-[140px]">
          {imagePath ? (
            <img
              src={`${API}/files/${imagePath}`}
              alt={label}
              style={{ width: size, height: size }}
              className="object-contain rounded-lg"
              data-testid="tile-preview-img"
            />
          ) : (
            <ImageIcon className="w-8 h-8 opacity-70" />
          )}
          <p className="font-bold text-sm" data-testid="tile-preview-label">{label}</p>
        </div>

        {/* Upload */}
        <label
          data-testid="tile-image-upload-area"
          className="block border-2 border-dashed border-[#3B82F6] hover:bg-[#E0F2FE] rounded-2xl p-4 mb-3 cursor-pointer text-center"
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#3B82F6]" />
          ) : (
            <>
              <Upload className="w-7 h-7 mx-auto text-[#3B82F6] mb-1" />
              <p className="text-xs font-bold text-[#1E3A5F]">اضغط لرفع صورة جديدة (PNG/JPG)</p>
              <p className="text-[10px] text-[#5C6E64]">أقل من 5 ميغا</p>
            </>
          )}
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="tile-image-upload-input" />
        </label>

        {/* Size slider */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-[#1E3A5F]">حجم الصورة</label>
            <span className="text-xs font-bold bg-[#E0F2FE] text-[#1E3A5F] px-2 py-0.5 rounded-full" dir="ltr">{size}px</span>
          </div>
          <input
            type="range"
            min="24"
            max="120"
            step="4"
            value={size}
            onChange={(e) => setSize(Number(e.target.value))}
            data-testid="tile-image-size-slider"
            className="w-full accent-[#3B82F6]"
          />
          <div className="flex justify-between text-[10px] text-[#5C6E64] mt-1">
            <span>صغير</span>
            <span>متوسط</span>
            <span>كبير</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={saving || uploading}
            data-testid="tile-image-save"
            className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2.5 rounded-full text-sm"
          >
            {saving ? "جاري الحفظ..." : "حفظ"}
          </button>
          {(imagePath || (current?.image_path)) && (
            <button
              onClick={clearImage}
              disabled={saving}
              data-testid="tile-image-clear"
              className="bg-[#E85D45] hover:bg-[#c14a36] disabled:opacity-50 text-white px-4 rounded-full text-sm flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              مسح
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TileImageEditor;
